import React, { useEffect, useRef, useState } from 'react'
import MemberProfile from '../components/Members/MemberProfile'
import MemberForm from '../components/Members/MemberForm'
import authApiService from '../services/authServices';
import { Link } from 'react-router-dom';
import Modal from '../utils/modalUtils';

function Members() {
   const selectedCity = JSON.parse(localStorage.getItem("selectedCity"));
   const cityId = selectedCity?.id;
   const defaultFormData = {
      cityId: cityId,
      first_name: "",
      last_name: "",
      male: "",
      dexterity: "",
      playing_preference: [],
      tournamentEntry: "",
      gteq_rating: "",
      eq_rating: "",
      lteq_rating: "",
      seasons_entered: 1,
   };
   const [memberData, setMemberData] = useState([]);
   const [formData, setFormData] = useState(defaultFormData);
   // Start in the loading state so "No members found" never flashes before the first response.
   const [isLoading, setIsLoading] = useState(true);
   const latestRequest = useRef(0);
   const [isSeasonOpenForRegistration, setIsSeasonOpenForRegistration] = useState(false);

   useEffect(() => {
      getMember();
   }, [cityId]);

   console.log("memeber", memberData)

   /**
    * Handles form input changes
    * @param {Event} e - The change event
    */
   const handleChange = (e) => {
      const { name, value, type, checked } = e.target;

      setFormData((prevState) => {
         if (type === "checkbox") {
            if (name === "matchPreference") {
               // Ensure `playing_preference` is always an array
               let updatedPreferences = Array.isArray(prevState.playing_preference) 
                  ? prevState.playing_preference 
                  : [];
               
               if (checked) {
                  // Add value if not already present
                  if (!updatedPreferences.includes(value)) {
                     updatedPreferences = [...updatedPreferences, value];
                  }
               } else {
                  // Remove value if present
                  updatedPreferences = updatedPreferences.filter((item) => item !== value);
               }
               return { ...prevState, playing_preference: updatedPreferences };
            }
            return { ...prevState, [name]: checked ? 1 : 0 };
         }
         return { ...prevState, [name]: value };
      });
   };



   /**
    * Maps frontend playing preference values to API expected values
    * API expects: "Single" (singular), "Doubles", "Mixed Doubles"
    * Frontend uses: "Singles" (plural), "Doubles", "Mixed Doubles"
    * API supports comma-separated values for filtering multiple preferences
    * @param {string|Array} preferences - Playing preference value(s) from form
    * @returns {string} Formatted playing preference for API (comma-separated if multiple)
    */
   const mapPlayingPreferenceToAPI = (preferences) => {
      if (!preferences || (Array.isArray(preferences) && preferences.length === 0)) {
         return '';
      }

      // Convert to array format
      let preferenceArray = [];
      if (Array.isArray(preferences)) {
         preferenceArray = preferences;
      } else if (typeof preferences === 'string') {
         // Handle comma-separated string
         if (preferences.trim() === '') {
            return '';
         }
         preferenceArray = preferences.split(',').map(p => p.trim()).filter(p => p !== '');
      } else {
         return '';
      }

      if (preferenceArray.length === 0) {
         return '';
      }
      
      // Map frontend values to API values
      // "Singles" → "Single", others stay the same
      const mappedPreferences = preferenceArray.map(pref => {
         const trimmedPref = pref.trim();
         // Handle both "Singles" (plural) and "Single" (already correct)
         if (trimmedPref === 'Singles' || trimmedPref === 'Single') {
            return 'Single';
         }
         // Ensure "Doubles" and "Mixed Doubles" are exactly as expected
         if (trimmedPref === 'Doubles') {
            return 'Doubles';
         }
         if (trimmedPref === 'Mixed Doubles') {
            return 'Mixed Doubles';
         }
         return trimmedPref;
      });

      // Remove duplicates and sort for consistency
      const uniquePreferences = [...new Set(mappedPreferences)];
      
      // Sort preferences in a consistent order: Single, Doubles, Mixed Doubles
      const preferenceOrder = ['Single', 'Doubles', 'Mixed Doubles'];
      const sortedPreferences = uniquePreferences.sort((a, b) => {
         const indexA = preferenceOrder.indexOf(a);
         const indexB = preferenceOrder.indexOf(b);
         // If not in order array, keep original order
         if (indexA === -1 && indexB === -1) return 0;
         if (indexA === -1) return 1;
         if (indexB === -1) return -1;
         return indexA - indexB;
      });

      // Return comma-separated string for API (no spaces after commas)
      // Examples: "Single", "Single,Doubles", "Single,Doubles,Mixed Doubles"
      return sortedPreferences.join(',');
   };

   /**
    * Fetches member data based on search criteria
    * If multiple playing preferences are selected, makes separate API calls and combines results
    * @param {Object} data - Optional search data, defaults to formData
    */
   const getMember = async (data) => {
      // Only the newest search may update the list / loading flag (older, slower ones are ignored).
      const requestId = ++latestRequest.current;
      try {
         setIsLoading(true);
         // Prepare the data to send
         const searchData = data ? data : formData;
         
         // Map playing_preference from frontend format to API format
         const mappedPreference = mapPlayingPreferenceToAPI(searchData.playing_preference);
         
         // Check if multiple preferences are selected
         const preferencesArray = mappedPreference ? mappedPreference.split(',').map(p => p.trim()).filter(p => p !== '') : [];
         
         let allMembers = [];
         
         if (preferencesArray.length > 1) {
            // Multiple preferences: make separate API calls for each and combine results
            const promises = preferencesArray.map(pref => {
               const payload = {
                  cityId: searchData.cityId || cityId,
                  first_name: searchData.first_name || '',
                  last_name: searchData.last_name || '',
                  male: searchData.male || '',
                  dexterity: searchData.dexterity || '',
                  playing_preference: pref, // Single preference per call
                  gteq_rating: searchData.gteq_rating || "",
                  eq_rating: searchData.eq_rating || "",
                  lteq_rating: searchData.lteq_rating || "",
                  seasons_entered: searchData.seasons_entered || 1
               };
               
               return authApiService.post("members", payload);
            });
            
            // Wait for all API calls to complete
            const responses = await Promise.all(promises);
            
            // Combine all results and remove duplicates based on player ID
            const memberMap = new Map();
            responses.forEach(response => {
               const data = response?.data;
               const members = (data && data.members) ? data.members : (Array.isArray(data) ? data : []);
               if (response?.code === 200 && data?.isSeasonOpenForRegistration !== undefined) {
                  setIsSeasonOpenForRegistration(data.isSeasonOpenForRegistration);
               }
               members.forEach(member => {
                  if (member?.id && !memberMap.has(member.id)) {
                     memberMap.set(member.id, member);
                  }
               });
            });
            
            allMembers = Array.from(memberMap.values());
         } else {
            // Single preference or empty: make single API call
            const payload = {
               cityId: searchData.cityId || cityId,
               first_name: searchData.first_name || '',
               last_name: searchData.last_name || '',
               male: searchData.male || '',
               dexterity: searchData.dexterity || '',
               playing_preference: mappedPreference || '',
               gteq_rating: searchData.gteq_rating || "",
               eq_rating: searchData.eq_rating || "",
               lteq_rating: searchData.lteq_rating || "",
               seasons_entered: searchData.seasons_entered || 1
            };
            
            // Log payload for debugging
            console.log("Members API Payload:", payload);
            
            const response = await authApiService.post("members", payload);
            console.log("Members API Response:", response);
            
            // Handle response data: API returns { members, isSeasonOpenForRegistration }
            const data = response?.data;
            if (response?.code === 200 && data) {
               if (data.members && Array.isArray(data.members)) {
                  allMembers = data.members;
                  if (data.isSeasonOpenForRegistration !== undefined) {
                     setIsSeasonOpenForRegistration(data.isSeasonOpenForRegistration);
                  }
               } else if (Array.isArray(data)) {
                  allMembers = data;
               }
            }
         }
         
         if (requestId !== latestRequest.current) return;
         setMemberData(allMembers);
      } catch (error) {
         if (requestId !== latestRequest.current) return;
         console.error("Error:", error);
         Modal.fire({
            icon: "error",
            title: "Error",
            text: error.response?.data?.message || "Failed to search members. Please try again.",
            confirmButtonText: "OK"
         });
         setMemberData([]);
      } finally {
         if (requestId === latestRequest.current) setIsLoading(false);
      }
   }


   const handleSubmit = (e) => {
      e.preventDefault();
      getMember();
   };

   const handleClear = () => {
      const clearedData = {
         ...defaultFormData,
         cityId: cityId, // Preserve current city
      };
      setFormData(clearedData);
      getMember(clearedData);
   };


   return (
      <div className="page-container col-12 mb-2 pb-0">
         <style>{`
            #members .black-box > form#search > .row {
              margin-left: -8px;
              margin-right: -8px;
            }
            #members .black-box > form#search > .row > [class*="col-"] {
              padding-left: 12px;
              padding-right: 12px;
              box-sizing: border-box;
            }
            #members .members-filter-actions-col {
              text-align: left;
            }
            #members .members-dexterity-row {
              margin-left: 0;
              margin-right: 0;
              margin-bottom: 14px;
              align-items: center;
            }
            #members .members-dexterity-row .search-col {
              margin-bottom: 0;
              color: #fff;
            }
            #members .members-dexterity-row label {
              margin: 0;
              color: #fff;
              font-weight: 400;
              white-space: nowrap;
            }
            #members .members-dexterity-row select {
              width: 100% !important;
              min-width: 0;
              max-width: 100%;
            }
           #members .member-search-actions {
            display: flex;
            flex-direction: row;
            align-items: end;
            gap: 10px;
            padding-top: 0;
            justify-content: end;
            min-height: 66px;
         }
            #members .member-search-actions .purple_btn,
            #members .member-search-actions .gray_btn {
              box-sizing: border-box;
              width: 70px;
              min-width: 70px;
              height: 30px;
              padding: 0;
              margin: 0;
              border: 1px solid transparent;
              line-height: 28px;
            }
            #members .member-search-actions .gray_btn {
              border-color: #fff;
            }
            /* Mock-up (MEMBERS OCT 18): four filter groups with equal gutters */
            @media (min-width: 992px) {
              #members .black-box > form#search > .row {
                display: grid;
                grid-template-columns: 306fr 15fr 186fr 60fr 260fr 73fr 274fr;
                column-gap: 0;
                margin: 0;
                padding: 0 5px 9px 23px;
              }
              #members .black-box > form#search > .row > div:nth-of-type(1) { grid-column: 1; }
              #members .black-box > form#search > .row > div:nth-of-type(2) { grid-column: 3; }
              #members .black-box > form#search > .row > div:nth-of-type(3) { grid-column: 5; }
              #members .black-box > form#search > .row > div:nth-of-type(4) { grid-column: 7; }
              #members .black-box > form#search > .row > [class*="col-"] {
                width: auto;
                max-width: none;
                flex: none;
                padding-left: 0;
                padding-right: 0;
              }
              #members .black-box > form#search > .row > input[type="hidden"] {
                display: none;
              }
              #members .black-box > form#search .row .row {
                min-height: 46px;
                align-items: center;
                margin-bottom: 0;
              }
              #members .black-box > form#search input[type="text"],
              #members .black-box > form#search select {
                height: 29px;
              }
              #members .black-box > form#search .members-dexterity-row {
                margin-bottom: 0;
                margin-top: -5px;
              }
              /* "Match Preference" shares a baseline with First Name / Rating >= */
              #members .black-box > form#search .row .row > .search-row:first-child {
                min-height: 46px;
                display: flex;
                align-items: center;
              }
              #members .black-box > form#search .member-search-actions {
                min-height: 86px;
                align-items: end;
                gap: 15px;
              }
            }
            @media (max-width: 991px) {
              #members .members-filter-actions-col {
                margin-top: 12px;
              }
            }
         `}</style>
         <div id="members">
            <div className="section">
               <div className="membersLeft">
                  <div className="search-row">
                  </div>
               </div>
               <div className="col-sm-12 shadow-sm black-box">
                  <form id="search" onSubmit={handleSubmit}>
                     <div className="row text-light">

                        <input type="hidden" name="sortingParameter" id="sortingParameter" />
                        <div className="col-lg-3 col-sm-6">
                           <div className="row">
                              <div className="search-col col-sm-4">
                                 {/* <a href="#" id="sortByFirstNameId">First Name</a> */}
                                 <label id="sortByFirstNameId">First Name</label>
                              </div>
                              <div className="search-col col-sm-8">
                                 <input id="firstname" name="first_name" type="text" value={formData.first_name} size="10" maxlength="10" onChange={handleChange} />
                              </div>
                           </div>
                           <div className="row">
                              <div className="search-col col-sm-4">
                                 {/* <a href="#" id="sortByLastNameId">Last Name</a> */}
                                 <label id="sortByLastNameId">Last Name</label>
                              </div>
                              <div className="search-col col-sm-8">
                                 <input id="lastname" name="last_name" type="text" value={formData.last_name} size="10" maxlength="10" onChange={handleChange} />
                              </div>
                           </div>
                           <div className="row">
                              <div className="search-col col-sm-4">
                                 Gender
                              </div>
                              <div className="search-col col-sm-8">
                                 <select id="gender" name="male" value={formData.male} onChange={handleChange}>
                                    <option> </option>
                                    <option value='1'>Male</option>
                                    <option value='0'>Female</option>
                                 </select>
                              </div>
                           </div>
                           {/* Dexterity moved to right column above Search/Clear */}
                        </div>

                        <div className="col-lg-3 col-sm-6">
                           <div className="row pl-0 pl-lg-5">
                              <div className="search-row col-sm-12">
                                 <div className="search-col">
                                    Match Preference
                                 </div>
                              </div>
                              <div className="search-row col-sm-12 match-list">
                                 <div className="search-col match-col">
                                    <div className="list-view-match text-left">
                                       <input
                                          id="matchPreference1"
                                          name="matchPreference"
                                          type="checkbox"
                                          value="Singles"
                                          checked={Array.isArray(formData.playing_preference) ? formData.playing_preference.includes("Singles") : false}
                                          onChange={handleChange}
                                       />
                                       <label className="checkboxlabel">Singles</label>
                                    </div>
                                    <div className="list-view-match text-left">
                                       <input
                                          id="matchPreference2"
                                          name="matchPreference"
                                          type="checkbox"
                                          value="Doubles"
                                          checked={Array.isArray(formData.playing_preference) ? formData.playing_preference.includes("Doubles") : false}
                                          onChange={handleChange}
                                       />
                                       <label className="checkboxlabel">Doubles</label>
                                    </div>
                                    <div className="list-view-match text-left">
                                       <input
                                          id="matchPreference3"
                                          name="matchPreference"
                                          type="checkbox"
                                          value="Mixed Doubles"
                                          checked={Array.isArray(formData.playing_preference) ? formData.playing_preference.includes("Mixed Doubles") : false}
                                          onChange={handleChange}
                                       />
                                       <label className="checkboxlabel">Mixed Doubles</label>
                                    </div>

                                 </div>
                              </div>
                           </div>
                        </div>

                        <div className="col-lg-3 col-sm-6">
                           <div className="row">
                              <div className="search-col col-sm-5">
                                 Rating ≥
                              </div>
                              <div className="search-col col-sm-7">
                                 <select id="minRating" name="gteq_rating" type="number" value={formData.gteq_rating} onChange={handleChange}>
                                    <option> </option>
                                    <option value='2.5'>2.5</option>
                                    <option value='3.0'>3.0</option>
                                    <option value='3.5'>3.5</option>
                                    <option value='4.0'>4.0</option>
                                    <option value='4.5'>4.5</option>
                                    <option value='5.0'>5.0</option>
                                 </select>
                              </div>
                           </div>

                           <div className="row">
                              <div className="search-col col-sm-5">
                                 Rating
                              </div>
                              <div className="search-col col-sm-7">
                                 <select id="rating" name="eq_rating" value={formData.eq_rating} onChange={handleChange}>
                                    <option value=""> </option>
                                    <option value="2.5">2.5</option>
                                    <option value="3.0">3.0</option>
                                    <option value="3.5">3.5</option>
                                    <option value="4.0">4.0</option>
                                    <option value="4.5">4.5</option>
                                    <option value="5.0">5.0</option>
                                 </select>
                              </div>
                           </div>

                           <div className="row">
                              <div className="search-col col-sm-5">
                                 Rating ≤
                              </div>
                              <div className="search-col col-sm-7">
                                 <select id="maxRating" name="lteq_rating" type="number" value={formData.lteq_rating} onChange={handleChange}>
                                    <option value="0"> </option>
                                    <option value='2.5'>2.5</option>
                                    <option value='3.0'>3.0</option>
                                    <option value='3.5'>3.5</option>
                                    <option value='4.0'>4.0</option>
                                    <option value='4.5'>4.5</option>
                                    <option value='5.0'>5.0</option>
                                 </select>
                              </div>
                           </div>
                        </div>

                        <div className="col-lg-3 col-sm-6 members-filter-actions-col">
                           <div className="row members-dexterity-row">
                              <div className="search-col col-sm-5">
                                 <label htmlFor="dexterity">Dexterity</label>
                              </div>
                              <div className="search-col col-sm-7">
                                 <select
                                    id="dexterity"
                                    name="dexterity"
                                    value={formData.dexterity}
                                    onChange={handleChange}
                                 >
                                    <option value=""> </option>
                                    <option value="Left Handed">Left Handed</option>
                                    <option value="Right Handed">Right Handed</option>
                                 </select>
                              </div>
                           </div>
                           <div className="member-search-actions">
                              <button type="submit" className="purple_btn">Search</button>
                              <button type="button" onClick={handleClear} className="gray_btn">Clear</button>
                           </div>
                        </div>
                     </div>
                  </form>
               </div>
            </div>
            <MemberForm
               memberData={memberData}
               isLoading={isLoading}
               isSeasonOpenForRegistration={isSeasonOpenForRegistration}
            />
         </div>
      </div>
   )
}

export default Members